import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import LessonIssueApiUtils from '@utils/api/lessonIssue';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type {
  ReportIssueModalContainerProps,
  IssueTypeOption,
} from './report-issue-modal.types';

export const useReportIssueModalService = (
  props: ReportIssueModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage, lesson } = props;

  const { t } = useTranslation('common');
  const { addFlash } = useFlashMessage();

  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const issueOptions: IssueTypeOption[] = useMemo(
    () => [
      {
        value: 'Student was absent',
        id: '0',
        content: t('issue.issueType.studentWasAbsent'),
      },
      {
        value: 'Teacher was absent',
        id: '1',
        content: t('issue.issueType.teacherWasAbsent'),
      },
      {
        value: 'Student-related technical problem',
        id: '2',
        content: t('issue.issueType.studentTechIssue'),
      },
      {
        value: 'Tutor-related technical problem',
        id: '3',
        content: t('issue.issueType.teacherTechIssue'),
      },
      { value: 'Other', id: '4', content: t('issue.other') },
    ],
    [t],
  );

  const handleIssueTypeChange = useCallback((value: string) => {
    setSelectedIssue(value);
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      if (selectedIssue === null) {
        addModalMessage(t('modals.issue.report.typeIsEmpty'), {
          type: 'error',
          autoDisappear: false,
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        const response = await LessonIssueApiUtils.report(
          lesson.id,
          selectedIssue,
          comment,
        );

        addFlash(response.message, {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        });
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t('errors.generic');
        addModalMessage(errorMessage, {
          autoDisappear: false,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [
      selectedIssue,
      comment,
      lesson.id,
      t,
      addModalMessage,
      clearModalMessage,
      addFlash,
      hideModal,
    ],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    selectedIssue,
    comment,
    issueOptions,
    isSubmitting,
    message: props.message,
    messageType: props.messageType,
    onIssueTypeChange: handleIssueTypeChange,
    onCommentChange: handleCommentChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
